<template>
  <div class="store-body">
    <b-row>
      <b-col md="3">
        <b-input-group class="shadow-sm rounded">
          <template slot="prepend">
            <div
              class="d-flex justify-content-center align-items-center px-2 bg-white"
            >
              <i class="bi bi-search text-muted" aria-hidden="true"></i>
            </div>
          </template>
          <b-form-input
            id="inventory-search"
            v-model="keyword"
            @keyup.native="debounceSearch"
            placeholder="Search"
            class="border-0"
          >
          </b-form-input>
        </b-input-group>
      </b-col>
      <b-col md="3">
        <b-form-select
          v-model="selectedPaymentStatus"
          :options="paymentStatusOptions"
          @change="getPaymentExpressHistory(1)"
        />
      </b-col>
      <b-col md="3">
        <date-range-picker
          :min-date="UNIX_DATE"
          icon="bi-calendar"
          @onDateRangeChange="handleDateRangeChange"
          show-clear-button
        />
      </b-col>
    </b-row>
    <b-row class="data-container table-sort">
      <b-col>
        <b-table
          :items="paymentExpressHistory"
          :fields="fields"
          responsive
          show-empty
          :empty-text="loading ? 'Loading...' : 'No records found.'"
        >
          <template slot="payment_status" slot-scope="data">
            <h6 class="mb-0">
              <b-badge v-if="data.value === 'success'" variant="success" class="py-2 btn-pill">
                Successful
              </b-badge>
              <b-badge v-if="data.value === 'failed'" variant="secondary" class="py-2 btn-pill">
                Unsuccessful
              </b-badge>
              <b-badge
                v-if="data.value === 'in_process'"
                variant="primary"
                class="py-2 btn-pill text-white"
              >
                Processing
              </b-badge>
              <b-badge
                v-if="data.value === 'pending'"
                variant="warning"
                class="py-2 btn-pill text-white"
              >
                Pending
              </b-badge>
            </h6>
          </template>
          <template slot="channel" slot-scope="data">
            <span v-if="data.value === 'rush_plus'">RUSH Plus</span>
            <span v-if="data.value === 'estore'">E-Store</span>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <div class="mt-1">
          <span class="pagination-indicator">
            {{ pagination.currentFirstItem }}-{{ pagination.currentLastItem }} of {{ pagination.maxPage }} page
          </span>
          <b-pagination
            hide-goto-end-buttons
            align="left"
            size="md"
            class="custom-pagination-arrow-only mb-0 ml-2"
            :total-rows="
              pagination.totalPerPage > 0
                ? pagination.maxPage * pagination.totalPerPage
                : 1
            "
            :limit="pagination.totalPerPage > 0 ? 3 : 1"
            :per-page="pagination.totalPerPage"
            v-model="pagination.currentPage"
            @change="getPaymentExpressHistory"
          >
          </b-pagination>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import DateRangePicker from "@/components/common/DateRangePicker";
import { mapState } from "vuex";
import { DateTime } from "luxon";
import commonMixins from "@/mixins/common";
import _ from "lodash";

export default {
  mixins: [commonMixins],
  data() {
    return {
      paymentStatusOptions: [
        {
          value: "all",
          text: "All Status",
        },
        {
          value: "success",
          text: "Successful",
        },
        {
          value: "failed",
          text: "Unsuccessful",
        },
        {
          value: "in_process",
          text: "Processing",
        },
        {
          value: "pending",
          text: "Pending",
        },
      ],
      selectedPaymentStatus: "all",
      fields: [
        {
          key: "transaction_no",
          label: "Transaction ID",
          sortable: true,
          thClass: "text-nowrap"
        },
        {
          key: "or_number",
          label: "OR Number",
          sortable: true,
          thClass: "text-nowrap"
        },
        {
          key: "payment_date",
          sortable: true,
          formatter: (value) =>
            value
              ? DateTime.fromSQL(value).toLocaleString(
                  DateTime.DATETIME_SHORT_WITH_SECONDS
                )
              : "",
          thClass: "text-nowrap"
        },
        {
          key: "customer_name",
          sortable: true,
          thClass: "text-nowrap"
        },
        {
          key: "amount",
          sortable: true,
          formatter: (value) =>
            value ? `₱ ${this.formatCurrency(value)}` : "",
        },
        {
          key: "discounted_amount",
          sortable: true,
          formatter: (value, key, item) => {
            if (item.voucher_list.length > 0 && value)
              return `₱ ${this.formatCurrency(value)}`;
            return "N/A";
          },
          thClass: "text-nowrap"
        },
        {
          key: "voucher_list",
          sortable: true,
          label: "Promo Code",
          formatter: (value) =>
            value && value.length > 0
              ? value.map((promo) => promo.code).join(", ")
              : "N/A",
          thClass: "text-nowrap"
        },
        {
          key: "points_used",
          label: "Points Redeemed",
          sortable: true,
          thClass: "text-nowrap",
          formatter: (value) => value ? new Intl.NumberFormat('en-US').format(value) : ''
        },
        {
          key: "earned_points",
          label: "Points Earned",
          sortable: true,
          thClass: "text-nowrap",
          formatter: (value) => value ? new Intl.NumberFormat('en-US').format(value) : ''
        },
        {
          key: "payment_status",
          sortable: true,
          thClass: "text-nowrap"
        },
        {
          key: "channel",
          sortable: true,
        },
      ],
      keyword: "",
      startDate: "",
      endDate: "",
    };
  },
  components: {
    DateRangePicker,
  },
  computed: {
    ...mapState("estoreOrders", [
      "paymentExpressHistory",
      "loading",
      "pagination",
    ]),
  },
  created() {
    this.getPaymentExpressHistory();
    this.debounceSearch = _.debounce(() => {
      this.getPaymentExpressHistory();
    }, 500);
  },
  methods: {
    handleDateRangeChange({ from, to }) {
      this.startDate = from;
      this.endDate = to;
      this.getPaymentExpressHistory();
    },
    async getPaymentExpressHistory(page = 1) {
      await this.$store.dispatch("estoreOrders/getPaymentExpressHistory", {
        page: page,
        filters: {
          keyword: this.keyword,
          payment_status: this.selectedPaymentStatus,
          date_range: {
            start_date: this.startDate,
            end_date: this.endDate,
          },
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.badge {
  font-family: MainFontRegular, sans-serif;
  width: 90px;
}
.badge-secondary {
  background-color: #87929d;
}
</style>